import { EpicPolicy } from '@/types/tower/Domain';
import { calculateILF, calculatePPM } from './tower/Calculations';
import { DateTime } from 'luxon';
import { GetPoliciesAsHierarchy } from '@/utilities/Policies';
import store from '@/store';
import { IsUserAClient, IsUserAnAdmin, IsUserABroker } from './AuthUtils';

function isNullOrUndefined<T>(value: T): boolean {
  return value === null || value === undefined;
}

function cleanSpecialChars(value: string): string {
  return value.replace(/[/\\.><:"|?*]/, '');
}

export function TransformPoliciesToCSV(policies: Array<EpicPolicy>, parentView = false, displayedColumns?: string[]): string {
  const isUserNotAClient = !IsUserAClient(store.state.user);

  const fieldList: { value: string, text: string }[] = [];
  fieldList.push({ value: 'isChildPolicy', text: 'Policy/Line' });
  fieldList.push({ value: 'year', text: 'Year' });
  fieldList.push({ value: parentView ? 'childCompany' : 'accountName', text: 'Account Name' });
  if (!parentView) {
    fieldList.push({ value: 'parentAccountName', text: 'Parent Account Name' });
  }
  fieldList.push({ value: 'accountAttributes', text: 'Client Attributes' });
  if (isUserNotAClient) {
    fieldList.push({ value: 'agencyName', text: 'Agency Name' });
  }
  fieldList.push({ value: 'policyType', text: 'Policy Type' });
  fieldList.push({ value: 'coverageType', text: 'Coverage Type' });
  fieldList.push({ value: 'product', text: 'Product' });
  if (isUserNotAClient) {
    fieldList.push({ value: 'lineStatusCode', text: 'Policy Status' });
  }
  fieldList.push({ value: 'description', text: 'Description' });
  fieldList.push({ value: 'accountState', text: 'Client State' });
  if (IsUserAnAdmin(store.state.user) || IsUserABroker(store.state.user)) {
    fieldList.push({ value: 'department', text: 'Department' });
    fieldList.push({ value: 'profitCenter', text: 'Profit Center' });
  }
  fieldList.push({ value: 'topLevelIndustry', text: 'SIC Industry' });
  fieldList.push({ value: 'genericIndustry', text: 'Industry' });
  fieldList.push({ value: 'ownershipType', text: 'Ownership Type' });
  fieldList.push({ value: 'carrierGroup', text: 'Carrier' });
  fieldList.push({ value: 'issuingCompanyName', text: 'Issuing Company Name' });
  fieldList.push({ value: 'carrierRating', text: 'AM Best Rating' });
  if (isUserNotAClient) {
    fieldList.push({ value: 'premiumPayableName', text: 'Premium Payable' });
    fieldList.push({ value: 'syndicate', text: 'Syndicate Number' });
  }
  fieldList.push({ value: 'leadSyndicate', text: 'Lead Syndicate' });
  if (isUserNotAClient) {
    fieldList.push({ value: 'billMode', text: 'Bill Mode' });
  }
  fieldList.push({ value: 'wholesaler', text: 'Wholesaler' });
  fieldList.push({ value: 'wholesalerGroup', text: 'Wholesaler Group' });
  if (isUserNotAClient) {
    fieldList.push({ value: 'underwriterName', text: 'Underwriter' });
  }
  fieldList.push({ value: 'aggregateLimit', text: 'Aggregate Limit' });
  fieldList.push({ value: 'occurrenceLimit', text: 'Occurrence Limit' });
  fieldList.push({ value: 'attachmentPoint', text: 'Attachment Point Value' });
  fieldList.push({ value: 'retention', text: 'Retention' });
  fieldList.push({ value: 'premium', text: 'Premium' });
  fieldList.push({ value: 'ppm', text: 'PPM' });
  fieldList.push({ value: 'annualizedPPM', text: 'PPM (Annualized)' });
  fieldList.push({ value: 'ilf', text: 'ILF' });
  if (isUserNotAClient) {
    fieldList.push({ value: 'commissionBilled', text: 'Commission Billed' });
    fieldList.push({ value: 'rebateAmt', text: 'Rebate Amount' });
    fieldList.push({ value: 'feesBilled', text: 'Fees Billed' });
    fieldList.push({ value: 'computedRevenue', text: 'Revenue' });
    fieldList.push({ value: 'auditAmt', text: 'Audit Amount' });
  }
  fieldList.push({ value: 'quotaShareTotal', text: 'Quota Share Total' });
  fieldList.push({ value: 'policyID', text: 'Policy Number' });
  fieldList.push({ value: 'effectiveDate', text: 'Effective Date' });
  fieldList.push({ value: 'expirationDate', text: 'Expiration Date' });
  if (isUserNotAClient) {
    fieldList.push({ value: 'enteredDate', text: 'Entered Date' });
    fieldList.push({ value: 'broker', text: 'Broker Name' });
  }
  fieldList.push({ value: 'sideAType', text: 'Side A' });
  fieldList.push({ value: 'runoff', text: 'Runoff' });
  if (isUserNotAClient) {
    fieldList.push({ value: 'peFlag', text: 'Layer' });
  }

  fieldList.push({ value: 'accountExecutiveRole', text: 'Account Executive' });
  fieldList.push({ value: 'accountManagerRole', text: 'Account Manager' });
  if (isUserNotAClient) {
    fieldList.push({ value: 'secondaryAccountManagerRole', text: 'Secondary Account Manager' });
  }
  fieldList.push({ value: 'brokerRole', text: 'Broker' });
  fieldList.push({ value: 'claimsRole', text: 'Claims' });
  fieldList.push({ value: 'clientLeadRole', text: 'Client Lead' });

  fieldList.push({ value: 'currentTermAmount', text: 'Current Term Amount'});
  fieldList.push({ value: 'obligeeName', text: 'Obligee Name'});
  fieldList.push({ value: 'obligeeAddress', text: 'Obligee Address'});
  fieldList.push({ value: 'principalName', text: 'Principal Name'});
  fieldList.push({ value: 'principalAddress', text: 'Principal Address'});
  fieldList.push({ value: 'underwritingName', text: 'Underwriting Name'});
  fieldList.push({ value: 'underwritingAddress', text: 'Underwriting Address'});
  fieldList.push({ value: 'cancelable', text: 'Cancelable'});
  fieldList.push({ value: 'netAmount', text: 'Net Amount'});

  const data = GetPoliciesAsHierarchy(policies).reduce((acc, p) => {
    acc.push(p);
    if (Array.isArray(p.children)) {
      p.children.forEach(c => acc.push({
        ...c,
        commissionBilled: undefined,
        rebateAmt: undefined,
        feesBilled: undefined,
        auditAmt: undefined,
        quotaAmount: undefined
      }));
    }
    return acc;
  }, <Array<EpicPolicy>>[]);

  const finalFieldList = displayedColumns 
    ? [fieldList[0], ...fieldList.filter(f => displayedColumns.includes(f.value) && f.value !== 'isChildPolicy')] 
    : fieldList;

  const policyData = data.map((x, i) => {
    return finalFieldList.map(f => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const text = x[f.value];
      if (f.value === 'isChildPolicy') {
        return text ? 'Line' : 'Policy';
      }
      if (f.value.includes('Date')) {
        if (text) {
          return DateTime.fromJSDate(new Date(text as string | number | Date), { zone: 'utc' }).toLocaleString(DateTime.DATE_FULL);
        } else {
          return '';
        }
      }

      if (f.value === 'runoff') {
        return text ? 'Yes' : '';
      }
      if (f.value === 'ppm') {
        return calculatePPM(x);
      }
      if (f.value === 'ilf' && !x.isChildPolicy) {
        return calculateILF(data, i) || '';
      }
      if (f.value === 'leadSyndicate') {
        const [num, agent] = [x.syndicateNumber, x.syndicateAgent];
        if (num && agent) {
          return `${num}-${agent}`;
        }
      }
      return text;
    }).map(y => isNullOrUndefined(y) ? '' : `"${normalizeValue(y as string | number | undefined)}"`).join(',');
  }).join('\n');

  const headers = finalFieldList.map(f => f.text);
  return `${headers}\n${policyData}`;
}

export function TransformAccountsToCSV(accounts: Array<any>): string {
  const fieldList = [
    { value: 'accountName', text: 'Name' },
    { value: 'displayName', text: 'Display Name' },
    { value: 'accountStatus', text: 'Status' },
    { value: 'agencies', text: 'CAC Entity' },
    { value: 'coverageTypes', text: 'Coverages' },
    { value: 'inForcePremium', text: 'In-Force Premium' },
    { value: 'revenue', text: 'Revenue' },
    { value: 'industryName', text: 'Industry' },
    { value: 'topLevelIndustry', text: 'SIC Industry' },
    { value: 'parentCompany', text: 'Parent Company' },
    { value: 'insertedDate', text: 'Client Since' },
    { value: 'ownershipType', text: 'Ownership Type' },
    { value: 'stockTicker', text: 'Stock Ticker' },
    { value: 'stockExchange', text: 'Stock Exchange' },
    { value: 'fortuneRank', text: 'Fortune Rank' },
  ];

  const accountData = accounts
    .map((x) => {
      return fieldList
        .map((f) => {
          let val = x[f.value];

          if (f.value === 'accountStatus') {
            val = val === 'P' ? 'Prospect' : val === 'I' ? 'Client' : val;
          }

          if (f.value === 'coverageTypes' && typeof val === 'string') {
            val = val
              .split(',')
              .filter((t: string) => t !== '')
              .map((t: string) => (t.includes('||') ? t.split('||')[0] : t))
              .sort((a, b) => a.localeCompare(b))
              .join(', ');
          }

          if (f.value === 'agencies' && typeof val === 'string') {
            val = val
              .split(',')
              .filter((t: string) => t !== '')
              .join(', ');
          }

          if (f.value === 'insertedDate' && val) {
            val = DateTime.fromISO(val, { zone: 'utc' }).toISODate();
          }

          return isNullOrUndefined(val) ? '' : `"${normalizeValue(val as string | number | undefined)}"`;
        })
        .join(',');
    })
    .join('\n');

  const headers = fieldList.map((f) => f.text).join(',');
  return `${headers}\n${accountData}`;
}

function normalizeValue(y: string | number | undefined): string {
  return ('' + y).replace(/"/g, '""');
}

export function DownloadCSV(data: string, filename: string): void {
  filename = cleanSpecialChars(filename);
  
  // Replace alternative dashes with hyphen to fix Excel import issue and avoid Excel format wizard
  const cleanedData = data.replace(/[\u002D\u2010-\u2015\u2212\uFE63\uFF0D]/g, '-');

  const file = new Blob([cleanedData], { type: 'text/csv' });
  const link = document.createElement('a');
  link.download = `${filename}.csv`;
  const url = window.URL.createObjectURL(file);
  link.href = url;
  link.textContent = 'Download Policy Table as CSV';
  link.dataset.downloadurl = ['text/csv', link.download, link.href].join(':');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function DownloadTableToCSV(tableSelector: string, filename: string, useHeaderTagTH = false): void {
  let data = '';
  let hasActions = false;
  const table = document.querySelector(tableSelector);
  if (table) {
    table.querySelectorAll('thead > tr').forEach(headerRow => {
      const row: string[] = [];

      headerRow.querySelectorAll(useHeaderTagTH ? 'th' : 'td').forEach(cell => {
        row.push(`"${cell.innerText}"`);
        for (let i = 1; i < cell.colSpan; i++) {
          row.push('');
        }
      });

      if (row[row.length - 1]?.toLowerCase().trim() === '"actions"') {
        row.pop();
        hasActions = true;
      }

      data += row.join(',') + '\n';
    });
    table.querySelectorAll('tbody > tr:not([class*="notcsv"])').forEach(bodyRow => {
      const row: string[] = [];
      bodyRow.querySelectorAll('td').forEach(cell => {
        const value = '' + (cell.hasAttribute('data-raw') ? cell.dataset.raw : cell.innerText);
        row.push(`"${value.replace('--', '').trim().replace(/\n/g, '')}"`);
      });
      if (hasActions) {
        row.pop();
      }
      data += row.join(',') + '\n';
    });
    table.querySelectorAll('tfoot > tr').forEach(footerRow => {
      const row: string[] = [];
      footerRow.querySelectorAll('td').forEach(cell => {
        row.push(`"${cell.innerText}"`);
      });
      data += row.join(',') + '\n';
    });
  }
  DownloadCSV(data, filename);
}
